import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ViewItemReorderInput } from '@deltek/specification-client/core/Models/projectProductFamily'
import { ViewItemReorderPTPInput } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Typography } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    templateData,
    templateFlags,
} from 'api/graphQL/reactiveVars/workspaceActions'
import { workspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import { EditableContentActionBar } from 'components/ActionBar'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { FamilyNumberFormat } from 'models/projectSettings'
import { getFormattedFamilyNumber } from 'pages/ProjectsPage/projectUtils'
import React from 'react'
import { getElementType } from 'shared/utils/workspaceUtils'
import AddRemoveProjectButton from './AddRemoveProjectButton'
import DeleteProductTypeButton from './DeleteProductTypeButton'
import DraftModeButton from './DraftModeButton'
import ProjectBar from './ProjectBar'
import { handleViewNotes } from './ProjectNotesDialog/notesUtils'
import DrawingTagField from './WorkspaceRightPane/DrawingTagField'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        borderBottom: '1px solid #DADADA',
        minHeight: 80,
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1),
        '@media (max-width: 1280px)': {
            flexWrap: 'wrap',
        },
    },
    elementTypeGridItemForTopic: {
        maxHeight: pxToRem(15),
        paddingRight: theme.spacing(1.25),
    },
    headerButtons: {
        display: 'flex',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        alignItems: 'center',
        justifyContent: 'flex-end',
        margin: 'auto',
        columnGap: pxToRem(10),
        '@media (max-width: 1600px)': {
            flexWrap: 'wrap',
            minWidth: pxToRem(225),
        },
        '@media (max-width: 1280px)': {
            paddingTop: theme.spacing(1),
        },
    },
    elementTitle: {
        display: 'flex',
        alignItems: 'center',
        '& .elementTitleContainer': {
            '@media (max-width: 1100px)': {
                flexWrap: 'wrap',
            },
        },
    },
    elementText: {
        fontSize: pxToRem(24),
        lineHeight: '28.13px',
    },
    elementType: {
        fontSize: pxToRem(14),
        lineHeight: pxToRem(16.41),
        fontWeight: 500,
        textTransform: 'capitalize',
    },
    drawingTagBox: {
        display: 'flex',
        flex: '1',
        minWidth: pxToRem(225),
    },
    notesText: {
        color: '#13B5EA',
        fontWeight: 600,
        fontSize: pxToRem(13),
        lineHeight: pxToRem(16.41),
    },
    elementTypeGridItem: {
        maxHeight: pxToRem(15),
        borderRight: '1px solid black',
        paddingRight: theme.spacing(1.25),
    },
    notesGridItem: {
        paddingLeft: theme.spacing(1),
        cursor: 'pointer',
    },
    roundContainer: {
        width: '122px',
        height: '38px',
        backgroundColor: '#314959',
        borderRadius: '20px',
        flexShrink: 0,
        display: 'flex',
        position: 'relative',
        right: '15px',
    },
    roundContainerActionBar: {
        width: pxToRem(156),
        height: pxToRem(38),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0, 1.875),
        backgroundColor: '#314959',
        borderRadius: '20px',
        flexShrink: 0,
        position: 'relative',
        right: '30px',
    },
    actionBarButton: {
        width: '24px',
        height: '24px',
        backgroundColor: '#314959',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    circleBackground: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
    },
}))

interface WorkspaceContentAreaHeaderProps {
    projectId?: string
    updateElementList?: (elementList: ProjectElementNode[]) => void
    famNumberFormat?: FamilyNumberFormat
    isOnlyAddedToProject?: boolean
    setIsAddRemoveClicked?: React.Dispatch<React.SetStateAction<boolean>>
    /**
     * Callback for updating the element list with a new (e.g., duplicate) product type
     *
     * @param element the new product type element to insert into the project element tree
     */
    insertNewProductTypeElement: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[]
    ) => void
    handleChangeFamilyName?: (
        updatedImportedFamilyName: string,
        importedProductFamilyId?: string
    ) => void
    isDraftMode?: boolean
    setIsDraftMode?: (val: boolean) => void
    drawingDesignation?: string
    selectedElementFormat?: string
    allElementsList?: ProjectElementNode[]
    getPersistedState?: (propKey: string) => any
    contentTeamFromStudioPage?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    handleUpdateSelectedTopic?: (
        topic?: Partial<ProductFamilyStudioManagement>
    ) => void
    handleFamilyReorderTemplates?: (
        reOrderFamilyContentInput: Partial<ViewItemReorderInput>
    ) => void
    handleProductTypeReorderTemplates?: (
        reOrderPTContentInput: Partial<ViewItemReorderPTPInput>
    ) => void
    projectNuggetId?: string
    isInProject: boolean | undefined
}
const WorkspaceContentAreaHeader: React.FC<WorkspaceContentAreaHeaderProps> = ({
    projectId = '',
    updateElementList,
    famNumberFormat = FamilyNumberFormat.XXXXXX,
    isOnlyAddedToProject = false,
    setIsAddRemoveClicked,
    drawingDesignation,
    insertNewProductTypeElement,
    selectedElementFormat,
    allElementsList,
    getPersistedState,
    contentTeamFromStudioPage,
    isDraftMode,
    setIsDraftMode,
    handleFamilyReorderTemplates,
    handleProductTypeReorderTemplates,
    selectedTopic,
    handleUpdateSelectedTopic,
    projectNuggetId,
    isInProject,
}) => {
    const classes = useStyles()
    const {
        selectedElementNode: selectedProjectElementNode,
        elementNotesCount,
    } = useReactiveVar(workspaceSelectedElement)
    const {
        projectProductFamilyId,
        nuggetId,
        contentItemNodes,
        dtv,
    } = useReactiveVar(workspaceContents)
    const { workspaceTemplateData } = useReactiveVar(templateData)
    const { isTemplateDataLastItem } = useReactiveVar(templateFlags)
    const {
        isReviewer,
        isAuthor,
        isSystemUser,
        isPublishingAuthor,
    } = useReactiveVar(userInfo)

    const elementType = React.useMemo(() => {
        if (selectedTopic) {
            return 'Sustainability Topic'
        } else {
            if (
                selectedElementFormat === 'DIVISIONS' &&
                selectedProjectElementNode?.elementType !== 'PRODUCTTYPE'
            ) {
                return selectedProjectElementNode?.elementType === 'ASSEMBLY'
                    ? 'Division'
                    : 'Section'
            } else {
                if (
                    contentTeamFromStudioPage &&
                    selectedProjectElementNode?.elementType !== 'ASSEMBLY'
                ) {
                    return 'Family'
                } else {
                    return getElementType(
                        selectedProjectElementNode?.elementType ?? ''
                    )
                }
            }
        }
    }, [
        selectedElementFormat,
        selectedProjectElementNode,
        selectedTopic,
        contentTeamFromStudioPage,
    ])

    const formattedFamNumber = React.useMemo(() => {
        const familyName = contentTeamFromStudioPage
            ? selectedProjectElementNode?.parentTreePath?.split('||')?.[
                  selectedProjectElementNode?.parentTreePath?.split('||')
                      ?.length - 1
              ]
            : null
        if (
            contentTeamFromStudioPage &&
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
        ) {
            return getFormattedFamilyNumber(
                familyName?.split('|')?.[0] ?? '',
                famNumberFormat
            )
        } else {
            return elementType !== 'Family'
                ? selectedProjectElementNode?.text.split('|')[0] ?? ''
                : getFormattedFamilyNumber(
                      selectedProjectElementNode?.text.split('|')[0] ?? '',
                      famNumberFormat
                  )
        }
    }, [
        elementType,
        famNumberFormat,
        selectedProjectElementNode,
        getFormattedFamilyNumber,
    ])
    const formattedFamilyName = React.useMemo(() => {
        if (
            contentTeamFromStudioPage &&
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
        ) {
            const familyName = contentTeamFromStudioPage
                ? selectedProjectElementNode?.parentTreePath?.split('||')?.[
                      selectedProjectElementNode?.parentTreePath?.split('||')
                          ?.length - 1
                  ]
                : null
            return familyName?.split('|')?.[1]
        } else return selectedProjectElementNode?.text.split('|')[1] ?? ''
    }, [selectedProjectElementNode, contentTeamFromStudioPage])

    const isExportButtonDisabled = React.useMemo(
        () =>
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE' &&
            (projectId === 'DraftMasterSpecProject' ||
                projectId === 'DraftSpecTextProject'),
        [selectedProjectElementNode, projectId]
    )

    const callViewNotes = React.useCallback(() => {
        handleViewNotes({
            projectId: projectId ?? '',
            getPersistedState: getPersistedState,
            selectedProjectElementNode: selectedProjectElementNode,
        })
    }, [projectId, selectedProjectElementNode, getPersistedState])

    const isDeleteProductTypeVisible =
        isDraftMode &&
        (isAuthor || isSystemUser) &&
        selectedProjectElementNode?.elementType === 'PRODUCTTYPE'

    const isDeleteProductTypeDisabled =
        !isPublishingAuthor ||
        (isPublishingAuthor &&
            (selectedProjectElementNode?.isBasedFromManufacturerOption ||
                false))

    return (
        <GridContainer
            item
            wrap="nowrap"
            className={clsx(classes.header, 'header')}
        >
            <GridItem
                className={classes.elementTitle}
                data-testid="elementTitle"
            >
                <GridContainer className="elementTitleContainer" wrap="nowrap">
                    {contentTeamFromStudioPage && selectedTopic ? (
                        <GridItem>
                            <Typography className={classes.elementText}>
                                {`${selectedTopic?.familyNumber ?? ''} ${
                                    selectedTopic?.name ?? ''
                                }`}
                            </Typography>
                            <GridItem
                                className={classes.elementTypeGridItemForTopic}
                            >
                                <Typography className={classes.elementType}>
                                    Sustainability Topic
                                </Typography>
                            </GridItem>
                        </GridItem>
                    ) : (
                        <GridItem>
                            <HoverTooltip
                                text={
                                    selectedProjectElementNode
                                        ? `${formattedFamNumber} ${formattedFamilyName}`
                                        : ''
                                }
                            >
                                <Typography className={classes.elementText}>
                                    {selectedProjectElementNode
                                        ? `${formattedFamNumber} ${formattedFamilyName}`
                                        : ''}
                                </Typography>
                            </HoverTooltip>
                            {selectedProjectElementNode && (
                                <GridContainer>
                                    <GridItem
                                        className={classes.elementTypeGridItem}
                                    >
                                        <Typography
                                            className={classes.elementType}
                                        >
                                            {elementType}
                                        </Typography>
                                    </GridItem>

                                    <GridItem
                                        className={classes.notesGridItem}
                                        onClick={callViewNotes}
                                    >
                                        <Typography
                                            className={classes.notesText}
                                        >
                                            {elementNotesCount} Note/s
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            )}
                        </GridItem>
                    )}

                    {!contentTeamFromStudioPage &&
                        selectedProjectElementNode?.elementType ===
                            'PRODUCTTYPE' &&
                        !selectedTopic &&
                        selectedProjectElementNode.elementId && (
                            <GridItem className={classes.drawingTagBox}>
                                <DrawingTagField
                                    tag={drawingDesignation ?? ''}
                                    elementId={
                                        selectedProjectElementNode.elementId as string
                                    }
                                />
                            </GridItem>
                        )}
                </GridContainer>
            </GridItem>
            <GridItem
                className={classes.headerButtons}
                xs={true}
                data-testid="workspaceButtonArea"
            >
                {(selectedProjectElementNode?.elementType !== 'ASSEMBLY' ||
                    selectedTopic) && (
                    <>
                        <EditableContentActionBar
                            handleFamilyReorderTemplates={
                                handleFamilyReorderTemplates
                            }
                            handleProductTypeReorderTemplates={
                                handleProductTypeReorderTemplates
                            }
                            projectNuggetId={projectNuggetId}
                            isInProject={isInProject}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            selectedTopic={selectedTopic}
                        />
                        {/* TODO: integrate <ProjectBar /> functionalities to the <EditableContentActionBar /> for future enhancements */}
                        <ProjectBar
                            projectId={projectId}
                            selectedProjectElementNode={
                                selectedProjectElementNode!
                            }
                            selectedTopic={selectedTopic}
                            isReviewer={isReviewer}
                            isContentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            insertNewProductTypeElement={
                                insertNewProductTypeElement
                            }
                            isExportButtonDisabled={isExportButtonDisabled}
                        />
                    </>
                )}

                {(selectedProjectElementNode?.elementType !== 'ASSEMBLY' ||
                    selectedTopic !== undefined) &&
                contentTeamFromStudioPage ? (
                    <>
                        <DraftModeButton
                            projectId={projectId as string}
                            projectElementNode={selectedProjectElementNode!}
                            updateElementList={updateElementList!}
                            setIsAddRemoveClicked={setIsAddRemoveClicked}
                            isDraftMode={isDraftMode}
                            setIsDraftMode={setIsDraftMode}
                            handleUpdateSelectedTopic={
                                handleUpdateSelectedTopic
                            }
                            selectedTopic={selectedTopic}
                        />
                        {isDeleteProductTypeVisible && (
                            <DeleteProductTypeButton
                                updateElementList={updateElementList!}
                                allElementsList={allElementsList}
                                isDisabled={isDeleteProductTypeDisabled}
                            />
                        )}
                    </>
                ) : (
                    !contentTeamFromStudioPage && (
                        <AddRemoveProjectButton
                            projectId={projectId}
                            projectElementNode={selectedProjectElementNode!}
                            updateElementList={updateElementList!}
                            isOnlyAddedToProject={isOnlyAddedToProject}
                            setIsAddRemoveClicked={setIsAddRemoveClicked}
                            selectedElementFormat={selectedElementFormat}
                            allElementsList={allElementsList}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                        />
                    )
                )}
            </GridItem>
        </GridContainer>
    )
}

export default WorkspaceContentAreaHeader
